import { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { apiGet } from '@/lib/api';
import { sanitize } from '@/lib/safeHtml';
import { ArrowRight, ArrowLeft, Mail, Phone, MapPin } from 'lucide-react';

const BRAND_LOGO = '/brand/logo.png';
const VALID_SLUGS = ['terms', 'privacy', 'refund', 'shipping', 'contact'];

// Minimal Markdown → HTML renderer — enough for h2/h3/strong/em/links/bullets/paragraphs.
// We deliberately avoid a dependency for a single page.
function renderMarkdown(md) {
  if (!md) return '';
  const lines = md.split('\n');
  const out = [];
  let inList = false;
  const flushList = () => { if (inList) { out.push('</ul>'); inList = false; } };
  for (let raw of lines) {
    const line = raw.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    if (/^###\s+/.test(line)) { flushList(); out.push(`<h3 class="font-display text-xl font-bold text-slate-900 mt-8 mb-3">${inline(line.replace(/^###\s+/, ''))}</h3>`); continue; }
    if (/^##\s+/.test(line)) { flushList(); out.push(`<h2 class="font-display text-2xl font-bold text-slate-900 mt-10 mb-4 tracking-tight">${inline(line.replace(/^##\s+/, ''))}</h2>`); continue; }
    if (/^-\s+/.test(line)) {
      if (!inList) { out.push('<ul class="list-disc pl-6 space-y-1.5 my-3 text-slate-700">'); inList = true; }
      out.push(`<li>${inline(line.replace(/^-\s+/, ''))}</li>`);
      continue;
    }
    if (!line.trim()) { flushList(); out.push(''); continue; }
    flushList();
    out.push(`<p class="text-slate-700 leading-[1.75] my-3">${inline(line)}</p>`);
  }
  flushList();
  return out.join('\n');
}

function inline(s) {
  return s
    .replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-slate-900">$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-indigo-600 hover:underline">$1</a>')
    .replace(/`([^`]+)`/g, '<code class="bg-slate-100 px-1.5 py-0.5 rounded text-[0.85em] font-mono">$1</code>');
}

export default function LegalPage() {
  const location = useLocation();
  const slug = (location.pathname || '/').replace(/^\//, '').split('/')[0];
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!VALID_SLUGS.includes(slug)) { setNotFound(true); return; }
    setData(null); setNotFound(false);
    apiGet(`/public/legal/${slug}`).then(setData).catch(() => setNotFound(true));
  }, [slug]);

  useEffect(() => {
    if (!data) return;
    document.title = `${data.title} | Gurukul Veda`;
    const setMeta = (name, content, attr = 'name') => {
      let el = document.head.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    setMeta('description', `${data.title} for Gurukul Veda by ${data.legal_info?.company_name}. Official and up to date.`);
  }, [data]);

  if (notFound) return (
    <div className="min-h-screen flex items-center justify-center bg-[#FEFBF4] px-6 text-center">
      <div>
        <h1 className="font-display text-3xl font-bold text-slate-900">Page not found</h1>
        <Link to="/" className="mt-4 inline-flex items-center gap-1.5 text-indigo-600 hover:underline">Back home <ArrowRight className="w-4 h-4" /></Link>
      </div>
    </div>
  );

  if (!data) return (
    <div className="min-h-screen flex items-center justify-center bg-[#FEFBF4]">
      <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const info = data.legal_info || {};

  return (
    <div className="min-h-screen bg-[#FEFBF4] text-slate-800" data-testid="legal-page" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#FEFBF4]/80 border-b border-slate-900/5">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-3.5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="legal-header-logo">
            <img src={BRAND_LOGO} alt="Gurukul Veda" className="w-9 h-9 object-contain" />
            <div>
              <p className="font-display text-[17px] font-semibold text-slate-900 leading-tight tracking-tight">Gurukul Veda</p>
              <p className="text-[10px] text-slate-500 leading-tight -mt-0.5">Your Personal Study Companion</p>
            </div>
          </Link>
          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900" data-testid="legal-back-btn">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        </div>
      </header>

      {/* Content */}
      <article className="max-w-3xl mx-auto px-5 sm:px-8 py-14" data-testid={`legal-content-${slug}`}>
        <nav className="text-xs text-slate-400 mb-4" data-testid="legal-breadcrumb">
          <Link to="/" className="hover:text-slate-700">Home</Link> · <span className="text-slate-500">{data.title}</span>
        </nav>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-slate-900 leading-[1.05] tracking-[-0.02em]" data-testid="legal-title">
          {data.title}
        </h1>
        {data.updated && <p className="mt-3 text-sm text-slate-500" data-testid="legal-updated">Effective from: {data.updated}</p>}
        <div
          className="legal-body mt-8 text-base"
          data-testid="legal-body"
          // SEC-003: renderMarkdown already HTML-escapes at line-start; sanitize()
          // adds DOMPurify allow-list as a defence-in-depth second layer.
          dangerouslySetInnerHTML={{ __html: sanitize(renderMarkdown(data.body)) }}
        />
      </article>

      {/* Business info card (always shown for payment-gateway compliance) */}
      <aside className="max-w-3xl mx-auto px-5 sm:px-8 pb-14">
        <div className="rounded-2xl bg-white border border-slate-100 p-6" data-testid="legal-business-card">
          <p className="font-heading text-[10px] uppercase tracking-[0.3em] text-slate-400 mb-3">Business details</p>
          <p className="font-display text-lg font-bold text-slate-900">{info.company_name}</p>
          <div className="mt-3 space-y-2 text-sm text-slate-600">
            <p className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 text-slate-400 flex-shrink-0" />
              <span>{info.address_line1}{info.address_line2 ? `, ${info.address_line2}` : ''}, {info.city}, {info.state} — {info.pincode}, {info.country}</span>
            </p>
            {info.support_email && <p className="flex items-center gap-2"><Mail className="w-4 h-4 text-slate-400" /><a href={`mailto:${info.support_email}`} className="text-indigo-600 hover:underline" data-testid="legal-email">{info.support_email}</a></p>}
            {info.support_phone && <p className="flex items-center gap-2"><Phone className="w-4 h-4 text-slate-400" /><a href={`tel:${info.support_phone.replace(/\s/g, '')}`} className="text-indigo-600 hover:underline" data-testid="legal-phone">{info.support_phone}</a></p>}
            {info.gstin && <p className="text-xs text-slate-400">GSTIN: {info.gstin}</p>}
          </div>
        </div>
      </aside>

      {/* Footer links */}
      <footer className="border-t border-slate-900/5 py-10">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 flex flex-wrap items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5">
            <img src={BRAND_LOGO} alt="" className="w-8 h-8" />
            <p className="font-display text-base font-semibold text-slate-900">Gurukul Veda</p>
          </Link>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-slate-500">
            <Link to="/terms" className="hover:text-slate-800" data-testid="footer-legal-terms">Terms</Link>
            <Link to="/privacy" className="hover:text-slate-800" data-testid="footer-legal-privacy">Privacy</Link>
            <Link to="/refund" className="hover:text-slate-800" data-testid="footer-legal-refund">Refund</Link>
            <Link to="/shipping" className="hover:text-slate-800" data-testid="footer-legal-shipping">Shipping</Link>
            <Link to="/contact" className="hover:text-slate-800" data-testid="footer-legal-contact">Contact</Link>
          </div>
          <p className="text-[11px] text-slate-400">© {new Date().getFullYear()} {info.company_name || 'Gurukul Veda'}</p>
        </div>
      </footer>
    </div>
  );
}
